/**
 * Mock of the passkey routes for `VITE_MOCK=1` (real routes:
 * apps/app/src/routes/passkeys.ts). In memory only. The mock accepts any
 * WebAuthn answer — there is no authenticator in mock mode, see
 * `lib/passkeys.ts` — but keeps the real step-up and limit rules.
 */
import type { PasskeySummary } from '../../types/api';

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

const MAX_PASSKEYS = 10;
const NAME_MAX = 60;
const DAY = 86_400_000;

function seed(): Map<string, PasskeySummary> {
  const now = Date.now();
  return new Map([
    [
      'pk-1',
      {
        id: 'pk-1',
        name: 'MacBook Touch ID',
        createdAt: now - 40 * DAY,
        lastUsedAt: now - 2 * DAY,
        backedUp: true,
      },
    ],
  ]);
}

let passkeys = seed();
let nextId = 2;
let pendingRegistration = false;
let enabled = true;

export function resetMockPasskeys(): void {
  passkeys = seed();
  nextId = 2;
  pendingRegistration = false;
  enabled = true;
}

/** Tests: the API with `PASSKEYS_ENABLED` off. */
export function setMockPasskeysEnabled(value: boolean): void {
  enabled = value;
}

function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const name = raw.trim().replace(/\s+/g, ' ').slice(0, NAME_MAX);
  return name.length > 0 ? name : null;
}

const MOCK_OPTIONS_REQUEST = {
  challenge: 'bW9jay1jaGFsbGVuZ2U',
  rpId: 'localhost',
  timeout: 300_000,
  userVerification: 'required',
  allowCredentials: [],
};

export function handleMockPasskeys(
  method: string,
  pathname: string,
  body: unknown,
  windowStepUp: (b: { totpCode?: string; recoveryCode?: string }) => Response | null,
): Response | null {
  if (!pathname.startsWith('/auth/passkeys')) return null;
  if (pathname === '/auth/passkeys/available' && method === 'GET') return json({ enabled });
  if (!enabled) return json({ error: 'not_found' }, 404);
  const b = (body ?? {}) as Record<string, unknown>;
  const stepUp = b as { totpCode?: string; recoveryCode?: string };

  if (pathname === '/auth/passkeys/login/options' && method === 'POST') {
    return json({ options: MOCK_OPTIONS_REQUEST });
  }
  if (pathname === '/auth/passkeys/login/verify' && method === 'POST') {
    const first = passkeys.values().next().value;
    if (!first) return json({ error: 'invalid_credentials' }, 401);
    first.lastUsedAt = Date.now();
    return json({ status: 'totp_required' });
  }

  if (pathname === '/auth/passkeys' && method === 'GET') {
    return json({ passkeys: Array.from(passkeys.values()) });
  }
  if (pathname === '/auth/passkeys/register/options' && method === 'POST') {
    const err = windowStepUp(stepUp);
    if (err) return err;
    if (passkeys.size >= MAX_PASSKEYS) return json({ error: 'passkey_limit_reached' }, 409);
    pendingRegistration = true;
    return json({
      options: {
        challenge: 'bW9jay1yZWdpc3Rlcg',
        rp: { name: 'NP-Flow', id: 'localhost' },
        user: { id: 'dS1taWtrZWw', name: 'mikkel@example.com', displayName: 'Mikkel' },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        timeout: 300_000,
        attestation: 'none',
        excludeCredentials: [],
        authenticatorSelection: { residentKey: 'required', userVerification: 'required' },
      },
    });
  }
  if (pathname === '/auth/passkeys/register/verify' && method === 'POST') {
    const err = windowStepUp(stepUp);
    if (err) return err;
    if (!pendingRegistration) return json({ error: 'passkey_invalid' }, 400);
    pendingRegistration = false;
    if (passkeys.size >= MAX_PASSKEYS) return json({ error: 'passkey_limit_reached' }, 409);
    const now = Date.now();
    const id = `pk-${nextId++}`;
    const row: PasskeySummary = {
      id,
      name: cleanName(b.name) ?? `Passkey added ${new Date(now).toISOString().slice(0, 10)}`,
      createdAt: now,
      lastUsedAt: null,
      backedUp: false,
    };
    passkeys.set(id, row);
    return json({ passkey: row }, 201);
  }

  const match = /^\/auth\/passkeys\/([^/]+)$/.exec(pathname);
  if (match) {
    const row = passkeys.get(match[1] as string);
    if (method === 'PATCH') {
      const name = cleanName(b.name);
      if (name === null) {
        return json({ error: 'invalid_request', message: 'Give the passkey a name.' }, 400);
      }
      if (!row) return json({ error: 'not_found' }, 404);
      row.name = name;
      return json({ status: 'renamed' });
    }
    if (method === 'DELETE') {
      const err = windowStepUp(stepUp);
      if (err) return err;
      if (!row) return json({ error: 'not_found' }, 404);
      passkeys.delete(row.id);
      return json({ status: 'removed' });
    }
  }
  return null;
}
